/**
 * Fetching and parsing the sparse cloud for one job.
 *
 * Three properties this owes the screen.
 *
 * **The buffer never enters React state.** It is fetched, parsed, and dropped
 * within one async function; only the parsed typed arrays survive, and those
 * are what the GPU uploads. Putting an `ArrayBuffer` in state would keep the
 * whole file alive for as long as the component, on top of the arrays made
 * from it, for no benefit - nothing re-renders when bytes change.
 *
 * **There is no base64 anywhere.** Base64 costs a third more bytes on the wire
 * and a full extra copy to decode, to move binary data through a text channel
 * that this API does not use.
 *
 * **A file too big to draw is refused honestly.** The metadata carries
 * `byte_size`, so the decision is made before the download starts rather than
 * after a browser tab has been wedged by it.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { getArtifactContent } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { ReconstructionArtifact } from "@/api/types";
import { parsePly, PlyParseError, type PointCloud } from "./ply";

/**
 * The largest artifact this screen will download and draw.
 *
 * A sparse cloud is 15 bytes per point in this format, so 48 MB is roughly
 * 3.3 million points - far beyond anything the current pipeline produces (the
 * largest measured is 23,473) and still comfortably drawable. Past it the
 * honest answer is a download link, not a frozen tab.
 */
export const MAX_VIEWABLE_BYTES = 48 * 1024 * 1024;

export type PointCloudState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; cloud: PointCloud }
  | { status: "too-large"; byteSize: number }
  | { status: "empty" }
  | { status: "failed"; message: string };

function messageFor(error: unknown): string {
  if (error instanceof PlyParseError) return error.message;
  if (error instanceof ApiError) {
    switch (error.kind) {
      case "not_found":
        // Also what an artifact belonging to somebody else looks like, by
        // design - the API refuses both the same way.
        return "Bu çıktı artık mevcut değil.";
      case "timeout":
        return "Çıktı zamanında indirilemedi.";
      case "network":
        return "Sunucuya ulaşılamadı.";
      case "unavailable":
      case "server":
        return "Sunucu bu çıktıyı şu anda veremedi.";
      default:
        return "Çıktı yüklenemedi.";
    }
  }
  return "Çıktı yüklenemedi.";
}

export function usePointCloud(
  jobId: string | null,
  artifact: ReconstructionArtifact | null,
): { state: PointCloudState; retry: () => void } {
  const [state, setState] = useState<PointCloudState>({ status: "idle" });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!jobId || !artifact) {
      setState({ status: "idle" });
      return;
    }
    if (artifact.byte_size <= 0) {
      setState({ status: "empty" });
      return;
    }
    if (artifact.byte_size > MAX_VIEWABLE_BYTES) {
      setState({ status: "too-large", byteSize: artifact.byte_size });
      return;
    }

    const controller = new AbortController();
    const current = ++generation.current;
    setState({ status: "loading" });

    getArtifactContent(jobId, artifact.id, { signal: controller.signal })
      .then((buffer) => {
        if (current !== generation.current || controller.signal.aborted) return;
        // Parsed here, inside the promise: `buffer` goes out of scope on the
        // next line and only the typed arrays are retained.
        const cloud = parsePly(buffer);
        if (cloud.count === 0) {
          setState({ status: "empty" });
          return;
        }
        setState({ status: "ready", cloud });
      })
      .catch((error: unknown) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState({ status: "failed", message: messageFor(error) });
      });

    return () => controller.abort();
  }, [jobId, artifact, attempt]);

  return { state, retry };
}
